import { Injectable } from "@angular/core";
import { PAETEPRoleType } from "../../model/enum/paetep-role-types.enum";
import { PermissionTypes } from "../../model/enum/permission-types.enum";
import { PermissionsHelperService } from "../../services/permissions-helper/permissions-helper.service";
import { USER_TYPES_DETAILS } from "../../supervision-management/interface/user-summary-details.interface";
import { PaetepRoles } from "../../model/enum/rubric/user-role-names.enum";
import { UserRoleHelper } from "../../model/helpers/user-role/user-role.helper";

export const ALL_ROLES_USER_TYPE: USER_TYPES_DETAILS = { id: -2, displayOrder: -2, displayName: 'All Roles', roleName: '' };
const ALL_ROLES_DIVIDER: USER_TYPES_DETAILS = { id: -1, displayOrder: -1, displayName: '--', roleName: '', disabled: true };

const USER_TYPES: USER_TYPES_DETAILS[] = [
    //Normal Filters
    { id: 0, displayOrder: 0, displayName: 'All Non-Supervisors', roleName: '', isSupervisorOnly: false },
    { id: 1, displayOrder: 1, displayName: 'Teacher', roleName: 'Teacher', roleType: PAETEPRoleType.Teacher, isSupervisorOnly: false },
    { id: 2, displayOrder: 2, displayName: 'NTP', roleName: 'NTPE', roleType: PAETEPRoleType.NTPE, isSupervisorOnly: false },
    { id: 3, displayOrder: 3, displayName: 'Nontenured', roleName: 'Nontenured', roleType: PAETEPRoleType.Nontenured, isSupervisorOnly: false },
    { id: 4, displayOrder: 4, displayName: 'Paraeducator', roleName: 'Paraeducator', roleType: PAETEPRoleType.Paraeducator, isSupervisorOnly: false },
    { id: 5, displayOrder: 5, displayName: 'Support Staff', roleName: 'SupportStaff', roleType: PAETEPRoleType.Support_Staff, isSupervisorOnly: false },
    { id: 101, displayOrder: 6, displayName: 'Emergency Cert', roleName: PaetepRoles.EmergencyCertified, roleType: PAETEPRoleType.Emergency_Cert, isSupervisorOnly: false },
    { id: 102, displayOrder: 7, displayName: 'Substitute', roleName: PaetepRoles.Substitute, roleType: PAETEPRoleType.Substitute, isSupervisorOnly: false },

    // id 6 is kept by divider

    //Admin Filters
    { id: 7, displayOrder: 101, displayName: 'All Supervisors', roleName: '', isSupervisorOnly: true },
    { id: 8, displayOrder: 102, displayName: 'Principals', roleName: 'Building_Principal', roleType: PAETEPRoleType.Principal, isSupervisorOnly: true },
    { id: 9, displayOrder: 103, displayName: 'NTP Supervisors', roleName: 'NTPE_Supervisor', roleType: PAETEPRoleType.NTPE_Supervisor, isSupervisorOnly: true },
    { id: 10, displayOrder: 104, displayName: 'Support Staff Supervisors', roleName: 'SupportStaff_Supervisor', roleType: PAETEPRoleType.Support_Staff_Supervisor, isSupervisorOnly: true },
    
    // Sup NPTE Supervisor
    { id: 12, displayOrder: 200, displayName: 'Sup Curr Instruction', roleName: PaetepRoles.SupCurrInstruction, roleType: PAETEPRoleType.Sup_Curr_Instruction, isSupervisorOnly: true },
    { id: 13, displayOrder: 201, displayName: 'Sup Pupil Services', roleName: PaetepRoles.SupPupilServices, roleType: PAETEPRoleType.Sup_Pupil_Services, isSupervisorOnly: true },
    { id: 14, displayOrder: 202, displayName: 'Sup Special Education', roleName: PaetepRoles.SupSpecEducation, roleType: PAETEPRoleType.Sup_Spec_Education, isSupervisorOnly: true },
    { id: 15, displayOrder: 203, displayName: 'Sup Vocational Education', roleName: PaetepRoles.SupVocEducation, roleType: PAETEPRoleType.Sup_Voc_Education, isSupervisorOnly: true },
    { id: 16, displayOrder: 204, displayName: 'NTP Other Supervisor', roleName: PaetepRoles.NTPEOtherSupervisor, roleType: PAETEPRoleType.NTPE_Other_Supervisor, isSupervisorOnly: true }
];

const SCHOOLDISTRCT_ADMIN: USER_TYPES_DETAILS = { id: 11, displayOrder: 105, displayName: 'School District Admins', roleName: 'SchoolDistrict_Admin', roleType: PAETEPRoleType.District_Admin, isSupervisorOnly: true };

const DIVIDER = { id: 6, displayOrder: 100, displayName: '--', roleName: '', isSupervisorOnly: false, disabled: true };

@Injectable()
export class SupervisionManagementHelpersService {
    constructor(
        private permissionsHelperService: PermissionsHelperService
    ) { }

    filterUserTypes(buildingId?: number, rolesToExclude: PAETEPRoleType[] = [PAETEPRoleType.District_Admin]) {
        const filteredUserTypes = USER_TYPES.filter(userType =>
            this.excludeFilter(userType, rolesToExclude)
            && (!userType.isSupervisorOnly || this.permissionsHelperService.doIHaveAllPermissions([PermissionTypes.PAETEP_SMUI_ADMIN_VIEW]))
            && ((userType.roleType !== undefined && !UserRoleHelper.isSecondaryRole(userType.roleType)) ?
                (buildingId !== undefined ?
                    this.permissionsHelperService.canIManageRoleForBuilding(buildingId, userType.roleType) :
                    this.permissionsHelperService.canIManageRole(userType.roleType)
                ) :
                this.isSupervisorOnlyFilter(userType.isSupervisorOnly)
            )
        );

        if (!rolesToExclude.includes(PAETEPRoleType.District_Admin)) {
            if (this.permissionsHelperService.doIHaveAllPermissions([PermissionTypes.PAETEP_SMUI_CAN_VIEW_ALL_SUPERVISORS])) {
                filteredUserTypes.push(SCHOOLDISTRCT_ADMIN);
            }
        }

        return this.sortAndAdjustUserTypes(filteredUserTypes);
    }

    filterUserTypes_ByRoles(rolesICanManage: PAETEPRoleType[] = [], rolesToExclude: PAETEPRoleType[] = [], includViewAll: boolean = true): USER_TYPES_DETAILS[] {
        const filteredUserTypes = [ALL_ROLES_USER_TYPE, ALL_ROLES_DIVIDER, ...USER_TYPES, SCHOOLDISTRCT_ADMIN].filter(userType =>
            this.excludeFilter(userType, rolesToExclude)
            && (userType.roleType !== undefined ? 
                    rolesICanManage.includes(userType.roleType) 
                    : (includViewAll && this.isSupervisorOnlyFilter(userType.isSupervisorOnly))
            )
        );

        return this.sortAndAdjustUserTypes(filteredUserTypes);
    }

    private excludeFilter =(userType: USER_TYPES_DETAILS, rolesToExclude: PAETEPRoleType[] = []) =>
        !userType.roleType || !rolesToExclude.includes(userType.roleType);

    public isSupervisorOnlyFilter =(isSupervisorOnly?: boolean) => {
        const canViewAllSupervisors = this.permissionsHelperService.doIHaveAllPermissions([PermissionTypes.PAETEP_SMUI_CAN_VIEW_ALL_SUPERVISORS]);
        const canViewNonSupervisors = this.permissionsHelperService.doIHaveAllPermissions([PermissionTypes.PAETEP_SMUI_CAN_VIEW_ALL_NON_SUPERVISORS]);

        if (isSupervisorOnly === undefined) {
            return canViewAllSupervisors && canViewNonSupervisors;
        }

        return isSupervisorOnly ? canViewAllSupervisors: canViewNonSupervisors;
    }

    private sortAndAdjustUserTypes(filteredUserTypes: USER_TYPES_DETAILS[]) {
        const dividedUserTypes = (
            filteredUserTypes.findIndex(userType => userType.displayOrder > DIVIDER.displayOrder) !== -1 &&
            filteredUserTypes.findIndex(userType => userType.displayOrder < DIVIDER.displayOrder) !== -1
        ) ? [...filteredUserTypes, DIVIDER] : filteredUserTypes;

        return dividedUserTypes.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    getDefaultUserType(userTypes: USER_TYPES_DETAILS[]) {
        return userTypes.length > 0 ? userTypes[0] : null;
    }
}